/**
 * Tasks — list, filter, drill in. Three tabs:
 *   - Live      polled, with pause/resume + a "N new rows" badge.
 *   - History   the same query without polling, for ad-hoc exploration.
 *   - Failures  pre-filtered to status=failed for triage.
 *
 * Filters and the page live in the URL (?org=, ?agent=, ?cap=, ?status=,
 * ?source=, ?success=, ?after=, ?before=, ?page=, ?size=) so any view is
 * shareable. A row opens the task's own screen and carries that query
 * string with it, so the screen's back link returns to this exact view.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { listAgents } from '@optum/api-client/endpoints/agents';
import { listCapabilities } from '@optum/api-client/endpoints/capabilities';
import { listOrganizations } from '@optum/api-client/endpoints/organizations';
import { listTasksPage } from '@optum/api-client/endpoints/tasks';
import type { Task, TaskSource, TaskStatus } from '@optum/api-client/types';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { ChipFilter, ListToolbar } from '../../ui/ListToolbar.js';
import { useCurrentOrgId } from '../../lib/use-current-org.js';
import { RECORD_PARAM, formatRecordParam, useRecordPanel } from '../../components/shell/record.js';
import { useOpenRecordPage } from '../../components/shell/record-page.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { Tabs, type TabItem } from '../../ui/Tabs.js';
import { useToast } from '../../ui/useToast.js';
import { absoluteDateTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { DataTable } from '../../ui/DataTable.js';
import { ListShape } from '../../ui/shapes.js';
import { FilterChip, Inline, Input, Stack, Typography } from '../../ui/finixt.js';
import { Select } from '../../ui/Select.js';
import { LinkedTaskCell } from './LinkedTaskCell.js';
import { NESTED_ROW_STYLE } from './linked-rows.js';
import { OUTCOME_WORD, OutcomeWord } from './OutcomeWord.js';
import { useLinkedRows, type LinkedRows } from './useLinkedRows.js';
import {
  buildListFilters,
  PAGE_SIZES,
  useTasksUrlState,
  withPage,
  type PageSize,
  type TasksTab,
} from './tasks-url-state.js';

const STATUSES: readonly TaskStatus[] = [
  'pending',
  'running',
  'streaming',
  'completed',
  'failed',
  'cancelled',
];
const SOURCES: readonly TaskSource[] = ['client', 'operator_test', 'linkage'];
// Work that has not finished. A streaming task is executing, so it counts.
const IN_FLIGHT = new Set<TaskStatus>(['pending', 'running', 'streaming']);
const SOURCE_CHIPS = [
  { key: 'all', label: 'All sources' },
  ...SOURCES.map((s) => ({ key: s, label: s })),
] as const;
const OUTCOME_CHIPS = [
  { key: 'any', label: 'any' },
  { key: 'true', label: OUTCOME_WORD.true.label },
  { key: 'false', label: OUTCOME_WORD.false.label },
  { key: 'none', label: OUTCOME_WORD.none.label },
] as const;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Width of a From or To field on the filter row, in pixels. */
const DATE_FIELD_WIDTH = 200;
/** Width of the live poll interval select: its longest label and the icon. */
const POLL_SELECT_WIDTH = 88;
/** Width of the rows-per-page select: three digits and the icon. */
const PAGE_SIZE_SELECT_WIDTH = 76;
const POLL_OPTIONS: readonly { label: string; ms: number }[] = [
  { label: '5s', ms: 5_000 },
  { label: '30s', ms: 30_000 },
  { label: 'off', ms: 0 },
];

export function TasksPage() {
  const toast = useToast();
  const url = useTasksUrlState();
  const { search } = useLocation();
  // Mounted on two routes. Under /orgs/:orgId the tenant comes from the path
  // and overrides anything in the query string — otherwise the workspace's
  // Tasks page lists every task on the platform and offers an org picker,
  // which is the exact thing having a workspace is supposed to remove.
  const scopedOrgId = useCurrentOrgId();
  const filters = useMemo(
    () => ({
      ...buildListFilters(url),
      ...(scopedOrgId ? { organization_id: scopedOrgId } : {}),
    }),
    [url, scopedOrgId],
  );

  const [pollMs, setPollMs] = useState<number>(5_000);
  const [paused, setPaused] = useState(false);

  const isLive = url.tab === 'live';
  const refetchInterval = isLive && !paused && pollMs > 0 ? pollMs : false;

  const tasks = useQuery({
    queryKey: qk.tasks(filters as Record<string, unknown>),
    queryFn: () => listTasksPage(filters),
    refetchInterval,
  });

  const newCount = useNewRowsBadge(tasks.data?.items, paused && isLive);

  const q = url.search.trim().toLowerCase();
  const rows = (tasks.data?.items ?? []).filter((t) => !q || t.id.toLowerCase().includes(q));
  const total = tasks.data?.total ?? 0;
  const linked = useLinkedRows(rows, refetchInterval);

  function exportRows(list: Task[]) {
    if (list.length === 0) {
      toast.info('Nothing to export', 'Adjust filters then try again.');
      return;
    }
    downloadCsv(list);
  }

  // Settings has its own page since Feature 87 (D3); the old platform link
  // opens it. Under a tenant the value falls back to the whole list.
  if (!scopedOrgId && url.tab === 'settings') return <Navigate to="/settings" replace />;
  const TABS: TabItem<TasksTab>[] = [
    { value: 'all', label: 'All' },
    { value: 'live', label: 'Live' },
    { value: 'history', label: 'History' },
    { value: 'failures', label: 'Failures' },
  ];

  return (
    <ListShape
      title="Tasks"
      subtitle={subtitleFor(rows, isLive, paused, pollMs)}
      menu={[
        {
          icon: 'refresh',
          label: 'Refresh',
          disabled: tasks.isFetching,
          onSelect: () => void tasks.refetch(),
        },
        {
          icon: 'download',
          label: 'Export CSV',
          sub: 'the rows on this page',
          disabled: tasks.isPending,
          onSelect: () => exportRows(rows),
        },
      ]}
    >
      <Tabs
        items={TABS}
        value={url.tab === 'settings' ? 'all' : url.tab}
        onChange={(v) => url.setTab(v)}
        style={{ marginBottom: 16 }}
      />

      <>
        {isLive && (
          <LiveControls
            pollMs={pollMs}
            setPollMs={setPollMs}
            paused={paused}
            setPaused={setPaused}
            newCount={newCount}
            onApply={() => tasks.refetch()}
          />
        )}

        <ListToolbar
          search={url.search}
          onSearch={(v) => url.patch({ search: v })}
          placeholder="Search task ids…"
          filters={
            <ChipFilter
              options={SOURCE_CHIPS}
              value={url.source[0] ?? 'all'}
              onChange={(next) =>
                url.patch({ source: next === 'all' ? [] : ([next] as TaskSource[]) })
              }
            />
          }
        />

        <FilterBar scopedOrgId={scopedOrgId} />

        {tasks.isPending && (
          <Padded>
            <LoadingBlock />
          </Padded>
        )}
        {tasks.isError && (
          <Padded>
            <ErrorBlock
              message={tasks.error instanceof Error ? tasks.error.message : 'Failed to load tasks'}
            />
          </Padded>
        )}
        {tasks.data && rows.length === 0 && (
          <Padded>
            <EmptyState tab={url.tab} />
          </Padded>
        )}
        {rows.length > 0 && (
          <TasksTable
            linked={linked}
            count={countText(rows.length, total, url.page, url.size, Boolean(q))}
            search={withPage(search, url.page)}
            pager={
              <Pager
                total={total}
                page={url.page}
                size={url.size}
                onPage={(page) => url.patch({ page })}
                onSize={(size) => url.patch({ size })}
              />
            }
          />
        )}
      </>
    </ListShape>
  );
}

/**
 * "101–150 of 583 tasks", or with an id search narrowing the page,
 * "3 of 101–150 of 583 tasks": the search is client-side, so it can only
 * ever hide rows of the page on screen.
 */
function countText(
  shown: number,
  total: number,
  page: number,
  size: PageSize,
  searched: boolean,
): string {
  if (total === 0) return '0 tasks';
  const first = (page - 1) * size + 1;
  const last = Math.min(page * size, total);
  const range = `${first}–${last} of ${total} tasks`;
  return searched ? `${shown} of ${range}` : range;
}

function subtitleFor(rows: Task[], isLive: boolean, paused: boolean, pollMs: number): string {
  const since = Date.now() - DAY_MS;
  const recent = (t: Task) => Date.parse(t.created_at) >= since;
  const inFlight = rows.filter((t) => IN_FLIGHT.has(t.status)).length;
  const failed = rows.filter((t) => t.status === 'failed' && recent(t)).length;
  const done = rows.filter((t) => t.status === 'completed' && recent(t)).length;

  const parts = [`${inFlight} in flight`, `${failed} failed in 24h`, `${done} completed in 24h`];
  if (isLive) {
    parts.push(
      paused ? 'updates paused' : pollMs > 0 ? `live · ${pollMs / 1000}s` : 'manual refresh',
    );
  }
  return parts.join(' · ');
}

// ── Live controls ─────────────────────────────────────────────────────

function LiveControls({
  pollMs,
  setPollMs,
  paused,
  setPaused,
  newCount,
  onApply,
}: {
  pollMs: number;
  setPollMs: (ms: number) => void;
  paused: boolean;
  setPaused: (v: boolean) => void;
  newCount: number;
  onApply: () => void;
}) {
  return (
    <Inline align="center" gap={7} wrap={false}>
      {paused && newCount > 0 && (
        <Button
          variant="accent"
          size="sm"
          onClick={() => {
            setPaused(false);
            onApply();
          }}
        >
          {newCount} new — resume
        </Button>
      )}
      {/* A pulsing dot beats the word "live": it is visible peripherally and
          stops moving the moment polling does. */}
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          fontSize: 'var(--text-meta-lg)',
          color: 'var(--text-muted)',
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 'var(--radius-pill)',
            background: paused || pollMs === 0 ? 'var(--text-faint)' : 'var(--ok)',
            animation:
              paused || pollMs === 0 ? undefined : 'pulse 1.5s var(--ease-standard) infinite',
          }}
        />
        {paused ? 'paused' : pollMs > 0 ? `live · ${pollMs / 1000}s` : 'manual'}
      </span>
      <Button
        variant="secondary"
        size="sm"
        icon={paused ? 'play_arrow' : 'pause'}
        onClick={() => setPaused(!paused)}
      >
        {paused ? 'Resume' : 'Pause'}
      </Button>
      <Select
        aria-label="Poll interval"
        size="sm"
        width={POLL_SELECT_WIDTH}
        value={String(pollMs)}
        onChange={(value) => setPollMs(Number(value))}
        options={[...POLL_OPTIONS.map((o) => ({ value: String(o.ms), label: o.label }))]}
      />
    </Inline>
  );
}

/**
 * When the user pauses the live stream, we keep refetching is off — but if
 * they re-enable it later, we want to show "N new" since the snapshot. We
 * track the row-count seen *at the moment of pause* and diff against the
 * current data. Resets on resume.
 */
function useNewRowsBadge(rows: Task[] | undefined, paused: boolean): number {
  const baseline = useRef<string | null>(null);
  useEffect(() => {
    if (!paused) baseline.current = null;
    else if (baseline.current === null) baseline.current = rows?.[0]?.id ?? '';
  }, [paused, rows]);
  if (!paused || !rows || baseline.current === null) return 0;
  const idx = rows.findIndex((r) => r.id === baseline.current);
  return idx < 0 ? rows.length : idx;
}

// ── Filters ───────────────────────────────────────────────────────────

function FilterBar({ scopedOrgId }: { scopedOrgId: string | null }) {
  const url = useTasksUrlState();
  const orgId = scopedOrgId ?? url.organizationId;
  const orgs = useQuery({
    queryKey: qk.orgs(),
    queryFn: () => listOrganizations(),
    enabled: !scopedOrgId,
  });
  const caps = useQuery({ queryKey: qk.capabilities(), queryFn: listCapabilities });
  const agents = useQuery({
    queryKey: qk.agents(orgId),
    queryFn: () => listAgents({ organization_id: orgId }),
    enabled: Boolean(orgId),
  });

  const hasFilter = Boolean(
    (!scopedOrgId && url.organizationId) ||
    url.agentId ||
    url.capabilityId ||
    url.status.length > 0 ||
    url.source.length > 0 ||
    url.success ||
    url.createdAfter ||
    url.createdBefore,
  );

  // Every tab but All fixes the status set, so the status chips would only
  // contradict it. Show what the tab locked instead of a control that lies.
  const lockedByTab =
    url.tab === 'failures'
      ? 'failed'
      : url.tab === 'live'
        ? 'pending · running · streaming'
        : url.tab === 'history'
          ? 'completed · cancelled'
          : null;

  return (
    <div style={{ marginBottom: 11, display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 11 }}>
        {!scopedOrgId && (
          <SelectField
            label="Organization"
            value={url.organizationId}
            onChange={(v) => url.patch({ organizationId: v, agentId: '' })}
            options={(orgs.data ?? []).map((o) => ({ label: o.name, value: o.id }))}
          />
        )}
        <SelectField
          label="Agent"
          value={url.agentId}
          onChange={(v) => url.patch({ agentId: v })}
          options={(agents.data ?? []).map((a) => ({ label: a.name, value: a.id }))}
          disabled={!orgId}
        />
        <SelectField
          label="Capability"
          value={url.capabilityId}
          onChange={(v) => url.patch({ capabilityId: v })}
          options={(caps.data ?? []).map((c) => ({ label: c.name, value: c.id }))}
        />
        <DateField
          label="From"
          value={url.createdAfter}
          onChange={(v) => url.patch({ createdAfter: v })}
        />
        <DateField
          label="To"
          value={url.createdBefore}
          onChange={(v) => url.patch({ createdBefore: v })}
        />
        {hasFilter && (
          <Button variant="ghost" icon="filter_alt_off" onClick={url.clearFilters}>
            Clear
          </Button>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7 }}>
        <ChipGroup
          options={STATUSES}
          values={url.status}
          onToggle={(v) => url.patch({ status: toggle(url.status, v) as TaskStatus[] })}
          {...(lockedByTab ? { locked: lockedByTab } : {})}
        />
        {/* The outcome is not a status, so no tab locks it: a completed
            task can still have failed at what it was asked to do. */}
        <span
          aria-hidden
          style={{
            marginLeft: 5,
            marginRight: 5,
            height: 16,
            width: 1,
            background: 'var(--border-hairline)',
          }}
        />
        <div
          role="group"
          aria-label="Outcome"
          style={{ display: 'flex', alignItems: 'center', gap: 7 }}
        >
          <Typography variant="eyebrow">Outcome</Typography>
          <ChipFilter
            options={OUTCOME_CHIPS}
            value={url.success || 'any'}
            onChange={(next) => url.patch({ success: next === 'any' ? '' : next })}
          />
        </div>
      </div>
    </div>
  );
}

function toggle(values: readonly string[], value: string): string[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

/**
 * Status and source are short closed sets, so chips beat a dropdown: the
 * current selection is readable without opening anything.
 */
function ChipGroup({
  options,
  values,
  onToggle,
  locked,
}: {
  options: readonly string[];
  values: readonly string[];
  onToggle: (value: string) => void;
  locked?: string | undefined;
}) {
  if (locked) return <FilterChip icon="lock" label={locked} active />;
  return (
    <>
      {options.map((opt) => (
        <FilterChip
          key={opt}
          label={opt}
          active={values.includes(opt)}
          onClick={() => onToggle(opt)}
        />
      ))}
    </>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: { label: string; value: string }[];
  disabled?: boolean;
}) {
  return (
    <Stack gap={7} style={{ width: 176 }}>
      <Typography variant="eyebrow">{label}</Typography>
      <Select
        searchable
        aria-label={label}
        value={value}
        onChange={(value) => onChange(value)}
        disabled={disabled}
        options={[
          { value: '', label: 'All' },
          ...options.map((o) => ({ value: o.value, label: o.label })),
        ]}
      />
    </Stack>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  // Wider than a select's 176: Chrome's date-and-time field needs the room, or
  // its picker icon runs into the input's right padding.
  return (
    <Stack gap={7} style={{ width: DATE_FIELD_WIDTH }}>
      <Typography variant="eyebrow">{label}</Typography>
      <Input
        aria-label={label}
        type="datetime-local"
        value={value ? toLocalInput(value) : ''}
        onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : '')}
      />
    </Stack>
  );
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// ── Table ─────────────────────────────────────────────────────────────

function TasksTable({
  linked,
  count,
  search,
  pager,
}: {
  /** The page's rows with any opened first task's linked tasks beneath it. */
  linked: LinkedRows;
  count: string;
  /** The list's query string with the page made explicit. A row writes the
   * record beside it, so the task screen, opened from the panel, can come
   * back to this exact view and walk it. */
  search: string;
  pager: ReactNode;
}) {
  const navigate = useNavigate();
  const panel = useRecordPanel();
  const openPage = useOpenRecordPage();
  const selectedKey = panel.current?.kind === 'task' ? panel.current.id : undefined;
  return (
    <DataTable
      count={count}
      hint="a row opens the task beside the list"
      pager={pager}
      rows={linked.rows}
      selectedKey={selectedKey}
      rowStyle={(t) =>
        linked.nested.has(t.id) && t.id !== selectedKey ? NESTED_ROW_STYLE : undefined
      }
      onRowClick={(t) => {
        const next = new URLSearchParams(search);
        next.set(RECORD_PARAM, formatRecordParam({ kind: 'task', id: t.id }));
        navigate({ search: `?${next.toString()}` }, { replace: true });
      }}
      onRowOpenPage={(t) => openPage({ kind: 'task', id: t.id })}
      columns={[
        {
          key: 'id',
          label: 'Task',
          mono: true,
          strong: true,
          render: (t) => (
            <LinkedTaskCell
              task={t}
              depth={linked.depth.get(t.id) ?? 0}
              open={linked.isOpen(t.id)}
              onToggle={() => linked.toggle(t.id)}
            />
          ),
        },
        {
          key: 'capability_id',
          label: 'Capability',
          render: (t) => (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/plugins?tab=capabilities`);
              }}
              style={{
                fontSize: 'var(--text-meta-lg)',
                color: 'var(--text-accent)',
                fontFamily: 'inherit',
              }}
              className="mono"
            >
              {t.capability_id}
            </button>
          ),
        },
        {
          key: 'source',
          label: 'Source',
          render: (t) => (
            <span
              style={{
                borderRadius: 'var(--radius-control)',
                border: '1px solid var(--border-hairline)',
                background: 'var(--surface-sunken)',
                paddingLeft: 7,
                paddingRight: 7,
                paddingTop: 5,
                paddingBottom: 5,
                fontSize: 'var(--text-meta-lg)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--text-muted)',
              }}
              className="mono"
            >
              {t.source}
            </span>
          ),
        },
        {
          key: 'status',
          label: 'Status',
          render: (t) => (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 11 }}>
              <StatusBadge status={t.status} vocabulary="task" />
              <OutcomeWord success={t.success} />
            </span>
          ),
        },
        {
          key: 'duration',
          label: 'Duration',
          align: 'right',
          mono: true,
          render: (t) => durationText(t),
        },
        {
          key: 'created_at',
          label: 'Created',
          align: 'right',
          mono: true,
          render: (t) => absoluteDateTime(t.created_at),
        },
      ]}
    />
  );
}

function Pager({
  total,
  page,
  size,
  onPage,
  onSize,
}: {
  total: number;
  page: number;
  size: PageSize;
  onPage: (page: number) => void;
  onSize: (size: PageSize) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / size));
  return (
    <Inline align="center" gap={7} wrap={false}>
      <Typography variant="eyebrow">Per page</Typography>
      <Select
        aria-label="Rows per page"
        size="sm"
        width={PAGE_SIZE_SELECT_WIDTH}
        value={String(size)}
        onChange={(value) => onSize(Number(value) as PageSize)}
        options={[...PAGE_SIZES.map((n) => ({ value: String(n), label: String(n) }))]}
      />
      <Button
        variant="secondary"
        size="sm"
        icon="chevron_left"
        aria-label="Previous page"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      />
      <span
        style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
        className="mono"
      >
        Page {page} of {pages}
      </span>
      <Button
        variant="secondary"
        size="sm"
        icon="chevron_right"
        aria-label="Next page"
        disabled={page >= pages}
        onClick={() => onPage(page + 1)}
      />
    </Inline>
  );
}

function durationText(t: Task): string {
  if (!t.started_at) return '—';
  const start = Date.parse(t.started_at);
  const end = t.completed_at ? Date.parse(t.completed_at) : Date.now();
  const ms = Math.max(0, end - start);
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.round(ms / 1000)} s`;
}

// ── Empty + export + util ─────────────────────────────────────────────

function EmptyState({ tab }: { tab: TasksTab }) {
  if (tab === 'failures') {
    return <EmptyBlock>No failures match the current filters. Quiet hour.</EmptyBlock>;
  }
  if (tab === 'live') {
    return <EmptyBlock>Nothing recent. Live updates will appear here.</EmptyBlock>;
  }
  return <EmptyBlock>No tasks match the current filters.</EmptyBlock>;
}

function downloadCsv(rows: Task[]): void {
  const header = [
    'id',
    'organization_id',
    'agent_id',
    'capability_id',
    'status',
    'source',
    'created_at',
    'started_at',
    'completed_at',
    'success',
    'failure_reason',
  ];
  const lines = [header.join(',')];
  for (const t of rows) {
    lines.push(
      [
        t.id,
        t.organization_id,
        t.agent_id,
        t.capability_id,
        t.status,
        t.source,
        t.created_at,
        t.started_at ?? '',
        t.completed_at ?? '',
        t.success === null ? '' : String(t.success),
        csvCell(t.failure_reason ?? ''),
      ].join(','),
    );
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tasks-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function csvCell(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

function Padded({ children }: { children: ReactNode }) {
  return <div style={{ padding: 22 }}>{children}</div>;
}
